import { types, flow, Instance } from 'mobx-state-tree';
import { getFormattedConstants } from 'utils';
import { Constants } from 'http-requests';
import { IConstant } from 'types';
import { ISelectValue } from 'components/ui';
import { RequestStateModel } from './general-models';

const ConstantModel = types.model({
  text: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
});

const TenantTicketConstantsModel = types.model({
  TicketType: types.optional(types.array(ConstantModel), []),
});

const constantsStatuses = types.model({
  tenantTicket: types.optional(RequestStateModel, {}),
  supportTicket: types.optional(RequestStateModel, {}),
  requests: types.optional(RequestStateModel, {}),
  areas: types.optional(RequestStateModel, {}),
  telephony: types.optional(RequestStateModel, {}),
  historyMeters: types.optional(RequestStateModel, {}),
  automation: types.optional(RequestStateModel, {}),
  documents: types.optional(RequestStateModel, {}),
  houses: types.optional(RequestStateModel, {}),
  overdueMeters: types.optional(RequestStateModel, {}),
  news: types.optional(RequestStateModel, {}),
  gis: types.optional(RequestStateModel, {}),
  executor: types.optional(RequestStateModel, {}),
  appeals: types.optional(RequestStateModel, {}),
  crystal: types.optional(RequestStateModel, {}),
});

export const ConstantsStore = types
  .model({
    statuses: types.optional(constantsStatuses, {}),
    supportTickets: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    tenantTicket: types.optional(TenantTicketConstantsModel, {}),
    requests: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    areas: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    news: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    telephony: types.optional(types.frozen<{ [propName: string]: any }>(), {}),
    historyMeters: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    automation: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    overdueMeters: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    documents: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    houses: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    gis: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    executor: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    appeals: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
    crystal: types.optional(
      types.frozen<{ [propName: string]: IConstant[] }>(),
      {}
    ),
  })
  .views((self) => ({
    getConstantsWithUnselectedVariant(constants: IConstant[]): ISelectValue[] {
      const result = getFormattedConstants(constants);

      result.unshift({
        value: '',
        label: 'Не выбрано',
      });

      return result;
    },
    get tenantTicketTypeOptions(): ISelectValue[] {
      return self.tenantTicket.TicketType.map(({ text, value }) => ({
        label: text.charAt(0).toUpperCase() + text.slice(1),
        value: value,
      }));
    },
    get supportTicketTypeOptions(): ISelectValue[] {
      return (
        self.supportTickets.TicketType?.map(({ text, value }) => ({
          label: text.charAt(0).toUpperCase() + text.slice(1),
          value: value,
        })) || []
      );
    },
  }))
  .actions((self) => ({
    getSupportTicketsConstants: flow(function* () {
      self.statuses.supportTicket.pending = true;
      self.statuses.supportTicket.error = false;
      self.statuses.supportTicket.fulfilled = false;

      try {
        const response: any = yield Constants.GetSupportTicketsConstants();
        self.supportTickets = response.results;
        self.statuses.supportTicket.fulfilled = true;

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.supportTicket.error = true;
      } finally {
        self.statuses.supportTicket.pending = false;
      }
    }),
    getTenantTicketConstants: flow(function* () {
      self.statuses.tenantTicket.pending = true;
      self.statuses.tenantTicket.error = false;
      self.statuses.tenantTicket.fulfilled = false;

      try {
        const { results } = yield Constants.GetTenantTicketsConstants();
        self.tenantTicket = results;
        self.statuses.tenantTicket.fulfilled = true;

        return results;
      } catch (e) {
        console.error(e);
        self.statuses.tenantTicket.error = true;
      } finally {
        self.statuses.tenantTicket.pending = false;
      }
    }),
    getRequestsConstants: flow(function* () {
      self.statuses.requests.fulfilled = false;
      self.statuses.requests.error = false;
      self.statuses.requests.pending = true;

      try {
        const response: any = yield Constants.GetRequestsConstants();
        self.requests = response.results;
        self.statuses.requests.fulfilled = true;

        return response;
      } catch (e) {
        self.statuses.requests.error = true;
        console.error(e);
      } finally {
        self.statuses.requests.pending = false;
      }
    }),
    getAreasConstants: flow(function* () {
      self.statuses.areas.fulfilled = false;
      self.statuses.areas.error = false;
      self.statuses.areas.pending = true;

      try {
        const response: any = yield Constants.GetAreaConstants();
        self.areas = response.results;
        self.statuses.areas.fulfilled = true;

        return response;
      } catch (e) {
        self.statuses.areas.error = true;
        console.error(e);
      } finally {
        self.statuses.areas.pending = false;
      }
    }),
    getTelephonyConstants: flow(function* () {
      self.statuses.telephony.fulfilled = false;
      self.statuses.telephony.error = false;
      self.statuses.telephony.pending = true;

      try {
        const response: any = yield Constants.GetTelephonyConstants();
        self.telephony = response.results;
        self.statuses.telephony.fulfilled = true;

        return response;
      } catch (e) {
        self.statuses.telephony.error = true;
        console.error(e);
      } finally {
        self.statuses.telephony.pending = false;
      }
    }),
    getHistoryMetersConstants: flow(function* () {
      self.statuses.historyMeters.fulfilled = false;
      self.statuses.historyMeters.error = false;
      self.statuses.historyMeters.pending = true;

      try {
        const response = yield Constants.GetHistoryMetersConstants();
        self.historyMeters = response.results;
        self.statuses.historyMeters.fulfilled = true;

        return response;
      } catch (e) {
        self.statuses.historyMeters.error = true;
        console.error(e);
      } finally {
        self.statuses.historyMeters.pending = false;
      }
    }),
    getAutomationConstants: flow(function* () {
      try {
        self.statuses.automation.loadingStates();
        const response = yield Constants.GetAutomationConstants();
        self.automation = response.results;
        self.statuses.automation.successStates();

        return response;
      } catch (e) {
        self.statuses.automation.errorStates();
        console.error(e);
      } finally {
        self.statuses.automation.finallyStates();
      }
    }),
    getDocumentsConstants: flow(function* () {
      self.statuses.documents.fulfilled = false;
      self.statuses.documents.error = false;
      self.statuses.documents.pending = true;

      try {
        const response = yield Constants.GetDocumentsConstants();
        self.documents = response.results;
        self.statuses.documents.fulfilled = true;
      } catch (e) {
        console.error(e);
        self.statuses.documents.error = true;
      } finally {
        self.statuses.documents.pending = false;
      }
    }),
    getHousesConstants: flow(function* () {
      self.statuses.houses.fulfilled = false;
      self.statuses.houses.error = false;
      self.statuses.houses.pending = true;

      try {
        const response = yield Constants.GetHousesConstants();
        self.houses = response.results;
        self.statuses.houses.fulfilled = true;
      } catch (e) {
        console.error(e);
        self.statuses.houses.error = true;
      } finally {
        self.statuses.houses.pending = false;
      }
    }),
    getOverdueMetersConstants: flow(function* () {
      self.statuses.overdueMeters.fulfilled = false;
      self.statuses.overdueMeters.error = false;
      self.statuses.overdueMeters.pending = true;

      try {
        const response = yield Constants.GetOverdueMetersConstants();
        self.overdueMeters = response.results;
        self.statuses.overdueMeters.fulfilled = true;

        return response;
      } catch (e) {
        self.statuses.overdueMeters.error = true;
        console.error(e);
      } finally {
        self.statuses.overdueMeters.pending = false;
      }
    }),
    getNewsConstants: flow(function* () {
      self.statuses.news.fulfilled = false;
      self.statuses.news.error = false;
      self.statuses.news.pending = true;

      try {
        const response = yield Constants.GetNewsConstants();
        self.news = response.results;
        self.statuses.news.fulfilled = true;
      } catch (e) {
        self.statuses.news.error = true;
        console.error(e);
      } finally {
        self.statuses.news.pending = false;
      }
    }),
    getGISConstants: flow(function* () {
      try {
        self.statuses.gis.loadingStates();
        const response = yield Constants.GetGISConstants();
        self.gis = response.results;
        self.statuses.gis.successStates();

        return response;
      } catch (e) {
        self.statuses.gis.errorStates();
        console.error(e);
      } finally {
        self.statuses.gis.finallyStates();
      }
    }),
    getExecutorConstants: flow(function* () {
      try {
        self.statuses.executor.loadingStates();
        const response = yield Constants.GetExecutorConstants();
        self.executor = response.results;
        self.statuses.executor.successStates();

        return response;
      } catch (e) {
        self.statuses.executor.errorStates();
        console.error(e);
      } finally {
        self.statuses.executor.finallyStates();
      }
    }),
    getAppealsConstants: flow(function* () {
      try {
        self.statuses.appeals.loadingStates();
        const response = yield Constants.GetAppealsConstants();
        self.appeals = response.results;
        self.statuses.appeals.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.appeals.errorStates();
      } finally {
        self.statuses.appeals.finallyStates();
      }
    }),
    getCrystalConstants: flow(function* () {
      try {
        self.statuses.crystal.loadingStates();
        const response = yield Constants.GetCrystalConstants();
        self.crystal = response.results;
        self.statuses.crystal.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.crystal.errorStates();
      } finally {
        self.statuses.crystal.finallyStates();
      }
    }),
  }));
export interface IConstantStore extends Instance<typeof ConstantsStore> {}
